import { useEffect, useState, useRef, useCallback } from "preact/hooks"
import { Button } from "@/components/ui/button.tsx"
import { ZoomIn, ZoomOut, RotateCw, Download, ChevronLeft, ChevronRight } from "lucide-react"
import * as pdfjsLib from "pdfjs-dist"
import { renderTextLayer } from "pdfjs-dist"
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.min.mjs?url"
import "pdfjs-dist/web/pdf_viewer.css"

// Set the worker source
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker

export interface BoundingBox {
  page: number // 1-based page number
  x: number // x coordinate in inches
  y: number // y coordinate in inches
  width: number // width in inches
  height: number // height in inches
}

export interface PageDimensions {
  [pageNumber: number]: [number, number] // [width, height] in inches
}

interface PdfViewerProps {
  s3Uri: string
  fileName?: string
  // Bounding box to highlight (if any)
  highlightBox?: BoundingBox | null
  // Page dimensions from the API (for accurate bounding box positioning)
  pageDimensions?: PageDimensions
  onClose?: () => void
}

const BASE_SCALE = 1.5 // Base rendering scale for quality

export function PdfViewer({
  s3Uri,
  fileName,
  highlightBox,
  pageDimensions,
}: PdfViewerProps) {
  const [pdfDoc, setPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [numPages, setNumPages] = useState(0)
  const [zoom, setZoom] = useState(1)
  const [rotation, setRotation] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [position, setPosition] = useState({ x: 0, y: 0 })

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const textLayerRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)

  // Store page viewport info for positioning overlays
  const [pageInfo, setPageInfo] = useState<{
    width: number
    height: number
    scale: number
  } | null>(null)

  // Load PDF document
  useEffect(() => {
    setIsLoading(true)
    setError(null)

    const pdfUrl = `/api/_s3?s3_uri=${encodeURIComponent(s3Uri)}`

    const loadPdf = async () => {
      try {
        const loadingTask = pdfjsLib.getDocument(pdfUrl)
        const pdf = await loadingTask.promise
        setPdfDoc(pdf)
        setNumPages(pdf.numPages)
        setCurrentPage(1)
        setIsLoading(false)
      } catch (err) {
        console.error("Failed to load PDF:", err)
        setError("Failed to load PDF")
        setIsLoading(false)
      }
    }

    loadPdf()

    return () => {
      // Cleanup
    }
  }, [s3Uri])

  // When highlight box changes, navigate to that page
  useEffect(() => {
    if (highlightBox && highlightBox.page > 0 && highlightBox.page <= numPages) {
      setCurrentPage(highlightBox.page)
    }
  }, [highlightBox, numPages])

  // Render current page
  const renderPage = useCallback(async () => {
    if (!pdfDoc || !canvasRef.current || !textLayerRef.current) return

    const canvas = canvasRef.current
    const textLayerDiv = textLayerRef.current
    const ctx = canvas.getContext("2d")
    if (!ctx) return

    try {
      const page = await pdfDoc.getPage(currentPage)
      const scale = BASE_SCALE * zoom
      const viewport = page.getViewport({ scale, rotation })

      // Account for device pixel ratio for sharp rendering on retina displays
      const dpr = window.devicePixelRatio || 1

      // Set canvas internal dimensions at higher resolution
      canvas.height = viewport.height * dpr
      canvas.width = viewport.width * dpr

      // Scale canvas back down with CSS for correct display size
      canvas.style.width = `${viewport.width}px`
      canvas.style.height = `${viewport.height}px`

      // Reset transform and scale the context to match the higher resolution
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

      setPageInfo({
        width: viewport.width,
        height: viewport.height,
        scale,
      })

      const renderContext = {
        canvasContext: ctx,
        viewport,
      }

      await page.render(renderContext).promise

      // Render text layer for text selection
      textLayerDiv.innerHTML = ""
      textLayerDiv.style.width = `${viewport.width}px`
      textLayerDiv.style.height = `${viewport.height}px`

      const textContent = await page.getTextContent()
      await renderTextLayer({
        textContentSource: textContent,
        container: textLayerDiv,
        viewport,
      })
    } catch (err) {
      console.error("Failed to render page:", err)
    }
  }, [pdfDoc, currentPage, zoom, rotation])

  useEffect(() => {
    renderPage()
  }, [renderPage])

  // Calculate overlay position for bounding box
  const getOverlayStyle = useCallback(() => {
    if (!highlightBox || !pageInfo || highlightBox.page !== currentPage) {
      return null
    }

    // Get page dimensions from API or estimate from PDF
    let pageWidth: number, pageHeight: number

    if (pageDimensions && pageDimensions[currentPage]) {
      [pageWidth, pageHeight] = pageDimensions[currentPage]
    } else {
      // Fallback: assume standard letter size (8.5 x 11 inches)
      pageWidth = 8.5
      pageHeight = 11
    }

    // Convert inches to pixels at current scale
    const pxPerInchX = pageInfo.width / pageWidth
    const pxPerInchY = pageInfo.height / pageHeight

    const left = highlightBox.x * pxPerInchX
    const top = highlightBox.y * pxPerInchY
    const width = highlightBox.width * pxPerInchX
    const height = highlightBox.height * pxPerInchY

    return {
      position: "absolute" as const,
      left: `${left}px`,
      top: `${top}px`,
      width: `${width}px`,
      height: `${height}px`,
      border: "3px solid #ef4444",
      backgroundColor: "rgba(239, 68, 68, 0.15)",
      pointerEvents: "none" as const,
      boxShadow: "0 0 0 2px rgba(239, 68, 68, 0.3)",
      borderRadius: "2px",
      zIndex: 10,
    }
  }, [highlightBox, pageInfo, currentPage, pageDimensions])

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.25, 3))
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.25, 0.5))
  const handleResetZoom = () => {
    setZoom(1)
    setPosition({ x: 0, y: 0 })
    setRotation(0)
  }
  const handleRotate = () => setRotation((prev) => (prev + 90) % 360)

  const handlePrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1))
  const handleNextPage = () =>
    setCurrentPage((prev) => Math.min(prev + 1, numPages))

  const handleMouseDown = (e: MouseEvent) => {
    // Allow text selection in the text layer - only pan when clicking outside it
    // or when holding Alt key
    const target = e.target as HTMLElement
    if (target.closest(".textLayer") && !e.altKey) {
      return // Let text selection work
    }
    e.preventDefault()
    setIsDragging(true)
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    })
  }

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return
    e.preventDefault()
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    })
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  const handleWheel = (e: WheelEvent) => {
    e.preventDefault()
    if (e.ctrlKey || e.metaKey) {
      const delta = e.deltaY > 0 ? -0.1 : 0.1
      setZoom((prev) => Math.max(0.5, Math.min(3, prev + delta)))
    } else {
      setPosition((prev) => ({
        x: prev.x - e.deltaX,
        y: prev.y - e.deltaY,
      }))
    }
  }

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    container.addEventListener("mousemove", handleMouseMove)
    container.addEventListener("mouseup", handleMouseUp)
    container.addEventListener("mouseleave", handleMouseUp)
    container.addEventListener("wheel", handleWheel, { passive: false })

    return () => {
      container.removeEventListener("mousemove", handleMouseMove)
      container.removeEventListener("mouseup", handleMouseUp)
      container.removeEventListener("mouseleave", handleMouseUp)
      container.removeEventListener("wheel", handleWheel)
    }
  }, [isDragging, dragStart])

  if (isLoading) {
    return (
      <div class="w-full h-full flex items-center justify-center">
        <div class="text-center">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-2"></div>
          <div>Loading PDF...</div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div class="w-full h-full flex items-center justify-center">
        <div class="text-center text-red-600">
          <p>{error}</p>
          <Button
            variant="outline"
            class="mt-4"
            onClick={() =>
              window.open(
                `/api/_s3?s3_uri=${encodeURIComponent(s3Uri)}`,
                "_blank"
              )
            }
          >
            <Download className="h-4 w-4 mr-2" />
            Download PDF
          </Button>
        </div>
      </div>
    )
  }

  const overlayStyle = getOverlayStyle()

  return (
    <div class="w-full h-full flex flex-col">
      {/* Toolbar */}
      <div class="flex items-center justify-between p-2 border-b bg-white">
        <div class="flex items-center gap-2">
          <Button
            variant="outline"
            // @ts-ignore
            size="sm"
            onClick={handleZoomOut}
            disabled={zoom <= 0.5}
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span class="text-sm font-medium min-w-[60px] text-center">
            {Math.round(zoom * 100)}%
          </span>
          <Button
            variant="outline"
            // @ts-ignore
            size="sm"
            onClick={handleZoomIn}
            disabled={zoom >= 3}
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            // @ts-ignore
            size="sm"
            onClick={handleRotate}
            title="Rotate 90"
          >
            <RotateCw className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            // @ts-ignore
            size="sm"
            onClick={handleResetZoom}
            title="Reset view"
          >
            Reset
          </Button>
        </div>

        <div class="flex items-center gap-2">
          {numPages > 1 && (
            <div class="flex items-center gap-1">
              <Button
                variant="outline"
                // @ts-ignore
                size="sm"
                onClick={handlePrevPage}
                disabled={currentPage <= 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span class="text-sm min-w-[80px] text-center">
                {currentPage} / {numPages}
              </span>
              <Button
                variant="outline"
                // @ts-ignore
                size="sm"
                onClick={handleNextPage}
                disabled={currentPage >= numPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
          <Button
            variant="outline"
            // @ts-ignore
            size="sm"
            onClick={() =>
              window.open(
                `/api/_s3?s3_uri=${encodeURIComponent(s3Uri)}`,
                "_blank"
              )
            }
            title="Open in new tab"
          >
            <Download className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* PDF Content */}
      <div
        ref={containerRef}
        class="flex-1 overflow-hidden relative bg-gray-200"
        onMouseDown={handleMouseDown}
        style={{ cursor: isDragging ? "grabbing" : "grab" }}
      >
        <div
          class="flex items-center justify-center min-h-full"
          style={{
            transform: `translate(${position.x}px, ${position.y}px)`,
            transition: isDragging ? "none" : "transform 0.1s ease-out",
          }}
        >
          <div class="relative shadow-lg bg-white">
            <canvas ref={canvasRef} />
            {/* Text layer for selection */}
            <div
              ref={textLayerRef}
              class="textLayer absolute top-0 left-0"
            />
            {/* Bounding box overlay */}
            {overlayStyle && (
              <div ref={overlayRef} style={overlayStyle} />
            )}
          </div>
        </div>
      </div>

      {/* Highlight indicator */}
      {highlightBox && highlightBox.page !== currentPage && numPages > 1 && (
        <div class="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-yellow-100 border border-yellow-400 rounded px-3 py-1 text-sm">
          Line item is on page {highlightBox.page}
          <button
            class="ml-2 text-blue-600 underline"
            onClick={() => setCurrentPage(highlightBox.page)}
          >
            Go to page
          </button>
        </div>
      )}
    </div>
  )
}
